import { z } from "zod";

/**
 * LA COULEUR D'UNE OPTION (statut, colonne de kanban, etiquette), le seul
 * vocabulaire de couleur partage par les apps (`docs/socle/cartes-kanban.md`,
 * section 3).
 *
 * Deux formes, et rien d'autre :
 * - une CLEF de la grille du design : les quatre tons d'avant (`neutral`,
 *   `accent`, `success`, `danger`) en tete, rendus exactement comme avant,
 *   puis seize teintes. Chaque clef est une variable CSS de `tokens.css`
 *   (`--couleur-<clef>`, ou le token d'origine pour les quatre premieres), qui
 *   porte sa variante sombre : la clef suit le theme ;
 * - un HEX (`#rgb` ou `#rrggbb`, casse libre), garde TEL QUEL : c'est le choix
 *   explicite d'une couleur precise, il ne suit pas le theme.
 *
 * Une config ecrite avant cette grille (un des quatre tons) n'a rien a migrer.
 * Le rendu (la variable CSS d'une clef) vit dans `@taki/ui`
 * (`packages/ui/src/lib/couleurs.ts`) : le modele ne connait que des noms.
 */
export type ClefDeCouleur =
  | "neutral"
  | "accent"
  | "success"
  | "danger"
  | "orange"
  | "amber"
  | "yellow"
  | "olive"
  | "emerald"
  | "teal"
  | "cyan"
  | "sky"
  | "blue"
  | "indigo"
  | "violet"
  | "plum"
  | "pink"
  | "coral"
  | "brown"
  | "slate";

/** Une clef de la grille, ou un hex garde tel quel. */
export type Couleur = ClefDeCouleur | `#${string}`;

/**
 * LA GRILLE, dans l'ordre d'affichage (5 colonnes, 4 rangees) : les quatre
 * tons d'avant en tete, puis le cercle chromatique, puis les deux neutres
 * chauds et froids.
 */
export const COULEURS_DU_DESIGN: readonly { clef: ClefDeCouleur; nom: string }[] = Object.freeze([
  { clef: "neutral", nom: "Gris" },
  { clef: "accent", nom: "Accent" },
  { clef: "success", nom: "Vert" },
  { clef: "danger", nom: "Rouge" },
  { clef: "orange", nom: "Orange" },
  { clef: "amber", nom: "Ambre" },
  { clef: "yellow", nom: "Jaune" },
  { clef: "olive", nom: "Olive" },
  { clef: "emerald", nom: "Émeraude" },
  { clef: "teal", nom: "Sarcelle" },
  { clef: "cyan", nom: "Cyan" },
  { clef: "sky", nom: "Ciel" },
  { clef: "blue", nom: "Bleu" },
  { clef: "indigo", nom: "Indigo" },
  { clef: "violet", nom: "Violet" },
  { clef: "plum", nom: "Prune" },
  { clef: "pink", nom: "Rose" },
  { clef: "coral", nom: "Corail" },
  { clef: "brown", nom: "Brun" },
  { clef: "slate", nom: "Ardoise" },
] as const);

const NOMS = new Map<string, string>(COULEURS_DU_DESIGN.map((c) => [c.clef, c.nom]));

/** `#rgb` ou `#rrggbb`, casse libre. Ni alpha, ni nom CSS (`red`). */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Un hex `#rgb` / `#rrggbb` (casse libre). */
export function estHex(v: unknown): v is `#${string}` {
  return typeof v === "string" && HEX.test(v);
}

/** Une clef de la grille, ou un hex. */
export function estCouleur(v: unknown): v is Couleur {
  return typeof v === "string" && (NOMS.has(v) || HEX.test(v));
}

/**
 * Le schema d'une couleur : une clef de la grille, ou un hex garde tel quel
 * (ni mis en minuscules, ni allonge). Tout le reste est refuse, avec la liste
 * des clefs dans le message : une couleur inconnue ne devient jamais un gris
 * silencieux.
 */
export const couleurSchema: z.ZodType<Couleur> = z.string().refine(estCouleur, {
  message: `Couleur inconnue : une clef (${COULEURS_DU_DESIGN.map((c) => c.clef).join(", ")}) ou un hex #rgb / #rrggbb`,
}) as unknown as z.ZodType<Couleur>;

/** Le nom affiche d'une couleur : celui de la grille, ou le hex en majuscules. */
export function nomDeCouleur(c: Couleur): string {
  return NOMS.get(c) ?? c.toUpperCase();
}
